/** Coordinator as returned inside `assignedCoordinatorId[]` by getAllEvents / getActiveCoordinators. */
export type DashboardCoordinator = {
  _id: string;
  fullName?: string;
  email?: string;
  profileImage?: string;
};

export type CoordinatorMatch = "any" | "all";

/** Dashboard coordinator filter: selected coordinators + how they combine (OR / AND). */
export type CoordinatorFilterValue = {
  selected: DashboardCoordinator[];
  match: CoordinatorMatch;
};

export const EMPTY_COORDINATOR_FILTER: CoordinatorFilterValue = {
  selected: [],
  match: "any",
};

const OBJECT_ID = /^[a-f\d]{24}$/i;

/** Most coordinators the filter sends at once (mirrors the backend cap). */
export const MAX_COORDINATOR_FILTER_IDS = 50;

/**
 * Keeps only well-formed entries: an object with a 24-hex `_id` and string-ish
 * display fields. Saved filters come back from the API as free JSON, so this
 * guards the render and the query string against a corrupted document.
 */
export function sanitizeCoordinatorSelection(
  input: unknown,
): DashboardCoordinator[] {
  if (!Array.isArray(input)) return [];
  const out: DashboardCoordinator[] = [];
  const seen = new Set<string>();
  for (const raw of input) {
    if (!raw || typeof raw !== "object") continue;
    const c = raw as Record<string, unknown>;
    const id = typeof c._id === "string" ? c._id : "";
    if (!OBJECT_ID.test(id) || seen.has(id)) continue;
    seen.add(id);
    out.push({
      _id: id,
      fullName: typeof c.fullName === "string" ? c.fullName : undefined,
      email: typeof c.email === "string" ? c.email : undefined,
      profileImage:
        typeof c.profileImage === "string" ? c.profileImage : undefined,
    });
    if (out.length >= MAX_COORDINATOR_FILTER_IDS) break;
  }
  return out;
}

export function sanitizeCoordinatorFilter(
  input: unknown,
): CoordinatorFilterValue {
  const v = (input ?? {}) as Record<string, unknown>;
  return {
    selected: sanitizeCoordinatorSelection(v.selected),
    match: v.match === "all" ? "all" : "any",
  };
}

export function isCoordinatorFilterActive(
  value?: CoordinatorFilterValue | null,
): boolean {
  return Boolean(value?.selected?.length);
}

/** Query params `getAllEvents` expects for this filter (empty object when inactive). */
export function coordinatorFilterToParams(
  value?: CoordinatorFilterValue | null,
): { coordinatorIds?: string; coordinatorMatch?: CoordinatorMatch } {
  if (!value || !isCoordinatorFilterActive(value)) return {};
  const ids = sanitizeCoordinatorSelection(value.selected).map((c) => c._id);
  if (!ids.length) return {};
  return {
    coordinatorIds: ids.join(","),
    coordinatorMatch: value.match === "all" ? "all" : "any",
  };
}

/**
 * `profileImage` is a free string on the user document (any role can set its
 * own), so only URLs from our storage are used as `<img src>` — anything else
 * falls back to initials instead of making the viewer's browser call it.
 */
export function safeAvatarSrc(url: unknown): string | undefined {
  if (typeof url !== "string" || !url) return undefined;
  try {
    const u = new URL(url, window.location.origin);
    if (u.protocol !== "https:" && u.protocol !== "http:") return undefined;
    const host = u.hostname.toLowerCase();
    const allowed =
      host.endsWith(".amazonaws.com") ||
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === window.location.hostname.toLowerCase();
    return allowed ? u.toString() : undefined;
  } catch {
    return undefined;
  }
}

export function coordinatorDisplayName(
  c?: DashboardCoordinator | null,
): string {
  return (c?.fullName || c?.email || "").trim();
}

export const COORDINATOR_AVATAR_SIZE = 28;
/** How much each avatar overlaps the previous one (px). */
export const COORDINATOR_AVATAR_OVERLAP = 6;
/** Left + right padding of a dashboard body cell (0.75rem each, see dashboardTableConfig). */
const CELL_HORIZONTAL_PADDING = 24;

/**
 * How many avatar "slots" fit side by side in a cell of the given width.
 * `CoordinatorAvatars` reserves the last slot for the "+N" chip on overflow.
 */
export function coordinatorSlotsForWidth(cellWidth?: number | null): number {
  if (!cellWidth || cellWidth <= 0) return 4;
  const usable = cellWidth - CELL_HORIZONTAL_PADDING;
  const step = COORDINATOR_AVATAR_SIZE - COORDINATOR_AVATAR_OVERLAP;
  return Math.max(1, Math.floor((usable + COORDINATOR_AVATAR_OVERLAP) / step));
}
